/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1690243200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Jul 25 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "mongodb-source",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Retrieve MongoDB credentials",
          "depth": 0,
          "id": "retrieve-mongodb-credentials"
        }, {
          "value": "Atlas",
          "depth": 1,
          "id": "atlas"
        }, {
          "value": "Community and Enterprise versions",
          "depth": 1,
          "id": "community-and-enterprise-versions"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Choose connection type",
          "depth": 1,
          "id": "choose-connection-type"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Model setup",
          "depth": 1,
          "id": "model-setup"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      strong: "strong",
      code: "code",
      h3: "h3",
      ol: "ol",
      li: "li",
      img: "img",
      ul: "ul",
      h4: "h4"
    }, _provideComponents(), props.components), {ConnectionTypeSnippet, NoCommonErrors} = _components;
    if (!ConnectionTypeSnippet) _missingMdxReference("ConnectionTypeSnippet", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you query data from collections and views stored in MongoDB and push it to downstream destinations. Most of the setup occurs in the Hightouch UI, but you need access to your MongoDB instance for information like your host, port, protocol, and credentials."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Make sure to allowlist Hightouch IP addresses so Hightouch can reach your cluster. Refer to our\n", _jsx(_components.a, {
            href: "/security/networking#ip-addresses",
            children: "IP address docs"
          }), " to find the relevant IP addresses for your Hightouch region. If you are using Atlas, you can add allowlist IP addresses from ", _jsx(_components.strong, {
            children: "Security > Network Access"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "retrieve-mongodb-credentials",
        children: "Retrieve MongoDB credentials"
      }), "\n", _jsxs(_components.p, {
        children: ["The user whose credentials you use must have ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/manual/reference/built-in-roles/#mongodb-authrole-readAnyDatabase",
          children: _jsx(_components.code, {
            children: "readAnyDatabase"
          })
        }), " permission for Hightouch to correctly query your MongoDB collections. To create a user in MongoDB, use ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/manual/reference/method/db.createUser/",
          children: _jsx(_components.code, {
            children: "db.createUser()"
          })
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "atlas",
        children: "Atlas"
      }), "\n", _jsx(_components.p, {
        children: "If you are using MongoDB Atlas, follow these instructions to retrieve your credentials:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to your cluster dashboard and click ", _jsx(_components.strong, {
              children: "Connect"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "destinations/destination-mongo-connect-button.png",
              alt: "MongoDB Connect"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Choose ", _jsx(_components.strong, {
              children: "Connect your application"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "destinations/destination-mongo-connect-app.png",
              alt: "MongoDB Connect Options"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Copy the hostname, username and connection options."
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "sources/source-mongo-credentials.png",
              alt: "MongoDB Connect URL"
            })
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "community-and-enterprise-versions",
        children: "Community and Enterprise versions"
      }), "\n", _jsxs(_components.p, {
        children: ["Use the hostname, username and password you use to connect to your MongoDB cluster. For more information, refer to the ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/mongodb-shell/connect/#connect-to-a-deployment",
          children: "MongoDB documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started in Higtouch, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "MongoDB"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-connection-type",
        children: "Choose connection type"
      }), "\n", _jsx(ConnectionTypeSnippet, {
        source: "MongoDB"
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your MongoDB server."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Protocol"
          }), ": Hightouch supports ", _jsx(_components.a, {
            href: "https://www.mongodb.com/docs/manual/reference/connection-string/#std-label-connections-standard-connection-string-format",
            children: "standard"
          }), " or ", _jsx(_components.a, {
            href: "https://www.mongodb.com/docs/manual/reference/connection-string/#std-label-connections-dns-seedlist",
            children: "SRV"
          }), " protocol to connect to your cluster. If you are using MongoDB Atlas and the connection string begins with protocol\n", _jsx(_components.code, {
            children: "mongodb+srv"
          }), ", choose the SRV protocol to connect."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your PostgreSQL server. The default port number is 27017, but yours may be different. You don't need to specify the port if you are using SRV protocol, as Hightouch uses the SRV record to look up hostname and port."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": This can be your personal MongoDB login or a dedicated user for Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": This is the password for the user specified above."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional)", _jsx(_components.strong, {
            children: "Connection options"
          }), ": You can specify ", _jsx(_components.a, {
            href: "https://www.mongodb.com/docs/manual/reference/connection-string/#connection-string-options",
            children: "connection options"
          }), " when connecting to your cluster. Use the format ", _jsx(_components.code, {
            children: "name=value"
          }), ", with each parameter separated by an ampersand ", _jsx(_components.code, {
            children: "&"
          }), ". For example, ", _jsx(_components.code, {
            children: "retryWrites=true&connectTimeoutMS=30000"
          }), ". See ", _jsx(_components.a, {
            href: "https://www.mongodb.com/docs/drivers/node/current/fundamentals/connection/connection-options/",
            children: "MongoDB's docs"
          }), " for a list of parameters."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up MongoDB as a source for the first time, Hightouch checks that you have the correct permissions. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from MongoDB."]
      }), "\n", _jsx(_components.h3, {
        id: "model-setup",
        children: "Model setup"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview page"]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add model."
          })]
        }), "\n", _jsx(_components.li, {
          children: "Select the MongoDB source you previously created."
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Database"
          }), " that contains the table you want to sync."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Collection/View"
          }), " you want to sync."]
        }), "\n", _jsxs(_components.li, {
          children: ["Optionally, enter a ", _jsx(_components.strong, {
            children: "Filter"
          }), " to apply to your query."]
        }), "\n", _jsx(_components.li, {
          children: "Preview your model's query results."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Name your model and select its ", _jsx(_components.a, {
            href: "/models/creating-models#unique-primary-key-requirement",
            children: _jsx(_components.strong, {
              children: "primary key"
            })
          }), ". Hightouch uses the primary key to determine which rows have been added, changed, or removed since the last sync."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "collections-and-views",
        children: "Collections and views"
      }), "\n", _jsxs(_components.p, {
        children: ["For advanced use cases, like joining data across collections or running aggregation pipelines, you can leverage ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/manual/core/views/#views",
          children: "views"
        }), " to pre-define the pipeline.\nAfter ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/manual/core/views/#create-and-manage-views",
          children: "creating a view"
        }), ", it automatically appears in the dropdown.\nIf you want to create a view that ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/v5.3/reference/method/db.createView/#create-a-view-from-a-single-collection",
          children: "only contains certain columns"
        }), " from your collection, you can use the ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/manual/reference/operator/aggregation/project/",
          children: "project operator"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "filters",
        children: "Filters"
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the MongoDB ", _jsx(_components.a, {
          href: "https://www.mongodb.com/docs/manual/tutorial/query-documents/",
          children: "Query documents documentation"
        }), " to learn how to use filters.\nFor example, to query all the documents with an ", _jsx(_components.code, {
          children: "account_id"
        }), " equal to ", _jsx(_components.code, {
          children: "20"
        }), ", you can set this filter: ", _jsx(_components.code, {
          children: "{ \"account_id\": 20 }"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(NoCommonErrors, {
        source: "true"
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
